import {amounts as globalAmounts} from '@pinecast/db-tip-jar-connect/currencies';
import type {DonationPage_podcast} from '@pinecast/gql/__generated_tj__/DonationPage';

const usdAmounts = globalAmounts.usd;

/**
 * The amounts a listener may pick, trimmed at the top by the show's tip limit.
 *
 * Note that amounts below `privateAccessMinSubscription` are *kept*. They used
 * to be dropped, which silently shortened the list for any show with a private
 * episode minimum — a listener who would have given $5 saw a page that started
 * at $10 and was told nothing about why.
 */
export function selectableAmounts(
  podcast: DonationPage_podcast,
): Array<number> {
  const amounts = [
    ...(globalAmounts[podcast.tipJarCurrency.toLowerCase()] ?? usdAmounts),
  ];
  if ((podcast.tipLimit ?? Infinity) < usdAmounts[5]) {
    amounts.pop();
  }
  if ((podcast.tipLimit ?? Infinity) < usdAmounts[4]) {
    amounts.pop();
  }
  return amounts;
}

/**
 * Whether this show gates any episodes behind a recurring tip. Private episodes
 * only exist for recurring subscribers, so a show that cannot take those has
 * nothing to gate no matter what minimum is configured.
 */
export function hasGatedEpisodes(podcast: DonationPage_podcast): boolean {
  return podcast.hasPrivateEpisodes && podcast.canHaveRecurringTips;
}

export type AmountLayout = {
  /** The three amounts promoted to cards, low to high. */
  tiers: Array<number>;
  /** Everything else, for the dropdown. */
  others: Array<number>;
  /** The amount that first unlocks private episodes, if any does. */
  threshold: number | null;
};

/**
 * Picks the three amounts to promote as cards.
 *
 * The minimum that unlocks private episodes is the interesting amount on the
 * page, so it takes the middle card and brings its neighbours with it. A show
 * with no minimum has no such anchor, so the middle of the range is used.
 */
export function layOutAmounts(podcast: DonationPage_podcast): AmountLayout {
  const amounts = selectableAmounts(podcast);
  const threshold = hasGatedEpisodes(podcast)
    ? podcast.privateAccessMinSubscription
    : null;

  const found = threshold === null ? -1 : amounts.indexOf(threshold);
  const middle = found === -1 ? Math.floor((amounts.length - 1) / 2) : found;
  const index = Math.min(Math.max(middle, 1), amounts.length - 2);

  const tiers = amounts.slice(index - 1, index + 2);
  return {
    tiers,
    others: amounts.filter(amount => !tiers.includes(amount)),
    threshold,
  };
}

/** Whether an amount is enough to unlock this show's private episodes. */
export function unlocksPrivateEpisodes(
  podcast: DonationPage_podcast,
  amount: number,
): boolean {
  if (!hasGatedEpisodes(podcast)) {
    return false;
  }
  return amount >= (podcast.privateAccessMinSubscription ?? 0);
}
